import React, { useContext, useEffect, useState, useCallback } from "react";
import { APIContext } from "../../../api";
import { Select } from "@chakra-ui/react";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";

/*
  AI website builder: which coding-agent CLI (and model) serves builds.

  The adapter list, the model lists, and the "is it usable" test all come from
  the builder itself through the admin API proxy — this screen never hard-codes
  a vendor's models. Saving runs a live health check first and refuses an
  unusable pick unless the admin forces it.
*/
function AIBuilderAdapter() {
  const {
    getAiBuilderAdapterState,
    getAiBuilderAdapterModels,
    validateAiBuilderAdapter,
    saveAiBuilderAdapter,
  } = useContext(APIContext);

  const [isLoading, setIsLoading] = useState(true);
  const [adapters, setAdapters] = useState([]); // [{id,label,models:[]}]
  const [engine, setEngine] = useState("");
  const [activeAdapter, setActiveAdapter] = useState("");
  const [activeModel, setActiveModel] = useState("");

  const [adapter, setAdapter] = useState("");
  const [model, setModel] = useState("");
  const [models, setModels] = useState([]);
  const [modelsSource, setModelsSource] = useState("");
  const [modelsLoading, setModelsLoading] = useState(false);

  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [verdict, setVerdict] = useState(null); // {ok,message,...}

  const loadModels = useCallback(
    async (adapterId, preferredModel) => {
      if (!adapterId) return;
      setModelsLoading(true);
      setModels([]);
      setModelsSource("");
      try {
        const res = await getAiBuilderAdapterModels(adapterId);
        const list = res?.models || [];
        setModels(list);
        setModelsSource(res?.source || "");
        // Keep the current model if it is still offered, else the default/first.
        const keep = list.some((m) => m.id === preferredModel)
          ? preferredModel
          : (list.find((m) => m.default) || list[0])?.id || "";
        setModel(keep);
      } catch (err) {
        setModels([]);
        toast.error("Could not load models for this adapter.");
      } finally {
        setModelsLoading(false);
      }
    },
    [getAiBuilderAdapterModels]
  );

  useEffect(() => {
    getAiBuilderAdapterState()
      .then(async (state) => {
        setAdapters(state?.available || []);
        setEngine(state?.engine || "");
        setActiveAdapter(state?.active_adapter || "");
        setActiveModel(state?.active_model || "");
        setAdapter(state?.active_adapter || "");
        await loadModels(state?.active_adapter, state?.active_model);
      })
      .catch(() => toast.error("Could not load the builder adapter settings."))
      .finally(() => setIsLoading(false));
  }, [getAiBuilderAdapterState, loadModels]);

  const onAdapterChange = (nextAdapter) => {
    setAdapter(nextAdapter);
    setVerdict(null);
    loadModels(nextAdapter, "");
  };

  const onModelChange = (nextModel) => {
    setModel(nextModel);
    setVerdict(null);
  };

  const onTest = async () => {
    setIsValidating(true);
    setVerdict(null);
    try {
      const v = await validateAiBuilderAdapter(adapter, model);
      setVerdict(v);
      if (v?.ok) toast.success("Adapter is usable.");
      else toast.error(v?.message || "Adapter is not usable.");
    } catch (err) {
      const msg = err?.response?.data?.error?.message || "The test could not run.";
      setVerdict({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setIsValidating(false);
    }
  };

  const onSave = async (force = false) => {
    setIsSaving(true);
    try {
      const { status, data } = await saveAiBuilderAdapter(adapter, model, force);
      if (status >= 200 && status < 300 && data?.saved) {
        setActiveAdapter(data.active_adapter);
        setActiveModel(data.active_model);
        setVerdict(data.verdict || { ok: true, message: "Saved." });
        toast.success(`Now using ${labelFor(data.active_adapter)} · ${data.active_model}.`);
      } else if (status === 409) {
        // Refused because the health check failed. Offer to force.
        setVerdict(data?.verdict || null);
        const msg = data?.error?.message || "The adapter is not usable.";
        toast.error(msg);
      } else {
        toast.error(data?.error?.message || "Could not save the selection.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Could not save the selection.");
    } finally {
      setIsSaving(false);
    }
  };

  const labelFor = (id) => adapters.find((a) => a.id === id)?.label || id;

  const isDirty = adapter !== activeAdapter || model !== activeModel;
  const showForce = verdict && verdict.ok === false;

  if (isLoading) {
    return (
      <div className="mt-8 flex items-center gap-3 text-gray-500">
        <PulseLoader size={10} /> <span>Loading builder settings…</span>
      </div>
    );
  }

  return (
    <div className="mt-10 max-w-[560px]">
      <h2 className="text-[22px] font-semibold">AI Website Builder</h2>
      <p className="mt-1 text-sm text-gray-500">
        Choose which coding-agent CLI and model build customer websites. Test a
        choice before saving — saving is blocked if the CLI is not installed or
        signed in.
      </p>

      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
        <span className="text-gray-500">Currently live:</span>{" "}
        <strong>{labelFor(activeAdapter)}</strong>
        <span className="text-gray-400"> · </span>
        <strong>{activeModel}</strong>
        {engine && engine !== "cli" ? (
          <div className="mt-1 text-xs text-amber-600">
            Note: the builder engine is set to “{engine}”, not “cli”. This adapter
            is used only when the CLI engine is active.
          </div>
        ) : null}
      </div>

      <label className="mt-6 block text-sm font-medium text-gray-700">Adapter</label>
      <Select
        className="mt-1"
        value={adapter}
        onChange={(e) => onAdapterChange(e.target.value)}
      >
        {adapters.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label}
          </option>
        ))}
      </Select>

      <div className="mt-4 flex items-center gap-2">
        <label className="block text-sm font-medium text-gray-700">Model</label>
        {modelsLoading ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600">
            <PulseLoader size={5} color="#4f46e5" />
            Loading models…
          </span>
        ) : modelsSource ? (
          <span className="text-xs font-normal text-gray-400">
            ({modelsSource === "live" ? "live from CLI" : "built-in list"}
            {models.length ? ` · ${models.length}` : ""})
          </span>
        ) : null}
      </div>
      <Select
        className="mt-1"
        value={model}
        onChange={(e) => onModelChange(e.target.value)}
        isDisabled={modelsLoading || models.length === 0}
        placeholder={modelsLoading ? "Loading models…" : undefined}
        opacity={modelsLoading ? 0.6 : 1}
      >
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label || m.id}
          </option>
        ))}
      </Select>

      {verdict ? (
        <div
          className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            verdict.ok
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          <div className="font-medium">
            {verdict.ok ? "Usable" : "Not usable"}
          </div>
          <div className="mt-0.5">{verdict.message}</div>
          {typeof verdict.installed === "boolean" ? (
            <div className="mt-1 text-xs opacity-80">
              installed: {String(verdict.installed)} · authenticated:{" "}
              {String(verdict.authenticated)}
              {verdict.version ? ` · ${verdict.version}` : ""}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onTest}
          disabled={isValidating || isSaving || !adapter}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
        >
          {isValidating ? "Testing…" : "Test adapter"}
        </button>

        <button
          type="button"
          onClick={() => onSave(false)}
          disabled={isSaving || isValidating || !adapter || !model || !isDirty}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save & activate"}
        </button>

        {showForce ? (
          <button
            type="button"
            onClick={() => onSave(true)}
            disabled={isSaving}
            className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            title="Save even though the health check did not pass"
          >
            Save anyway
          </button>
        ) : null}

        {isDirty ? (
          <span className="text-xs text-amber-600">Unsaved change</span>
        ) : null}
      </div>
    </div>
  );
}

export default AIBuilderAdapter;
